import useSWR from 'swr';
import React, { useState } from 'react';
import { varAlpha } from 'minimal-shared/utils';

import { outlinedInputClasses } from '@mui/material/OutlinedInput';
import { Box, Stack, Select, Button, MenuItem } from '@mui/material';

import { useSearchParams } from 'src/routes/hooks';

import { statusOptions } from 'src/pages/jobs/organisms/types';
import { fetcher, endpoints, updateCall, deleteFetcher } from 'src/lib/axios';

import { toast } from 'src/components/snackbar';
import { Iconify } from 'src/components/iconify';

/** Header Follow toggle — self-contained: reads the job id from the URL and follows/unfollows via the
 * job-followers API. Following a job is the notification subscription for that job. */
function JobFollowButton({ fullWidth }: { fullWidth?: boolean }) {
  const jobId = useSearchParams().get('jobId') || '';
  const { data: isFollowing, mutate } = useSWR<boolean>(
    jobId ? endpoints.job_followers.isFollowing(jobId) : null,
    fetcher
  );
  const [busy, setBusy] = useState(false);

  const toggle = async () => {
    if (!jobId || busy) return;
    setBusy(true);
    try {
      if (isFollowing) {
        await deleteFetcher(endpoints.job_followers.unfollow(jobId));
        toast.success('Unfollowed', { description: 'You will no longer get updates for this job.' });
      } else {
        await updateCall(endpoints.job_followers.follow(jobId), {}, {}, 'POST');
        toast.success('Following', { description: 'You will get updates for this job.' });
      }
      await mutate();
    } catch {
      toast.error('Failed', { description: 'Could not update your follow. Please try again.' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button
      variant={isFollowing ? 'contained' : 'outlined'}
      color="primary"
      onClick={toggle}
      disabled={busy || !jobId}
      fullWidth={fullWidth}
      startIcon={<Iconify icon={isFollowing ? 'eva:checkmark-fill' : 'eva:plus-fill'} />}
      sx={isFollowing ? undefined : { color: 'common.black', backgroundColor: 'common.white', '&:hover': { backgroundColor: 'grey.200' } }}
    >
      {isFollowing ? 'Following' : 'Follow'}
    </Button>
  );
}

/** Outlined Select border lives on the notched outline; theme default uses primary on hover/focus. */
function statusSelectNotchedOutline(borderDefault: string, borderEmphasis: string) {
  return {
    [`& .${outlinedInputClasses.notchedOutline}`]: { borderColor: borderDefault },
    [`&:hover .${outlinedInputClasses.notchedOutline}`]: { borderColor: borderEmphasis },
    [`&.Mui-focused .${outlinedInputClasses.notchedOutline}`]: { borderColor: borderEmphasis },
  } as const;
}

export const MainLayoutRightArea = React.memo(
  ({
    status,
    onViewLivePage,
    onStatusChange,
    renderInMenu = false,
  }: {
    status: string;
    onViewLivePage: () => void;
    onStatusChange: (status: string) => void;
    renderInMenu?: boolean;
  }) => {
    const rightAreaContent = (
      <>
        <JobFollowButton fullWidth={renderInMenu} />
        <Button
          variant="outlined"
          onClick={() => onViewLivePage()}
          fullWidth={renderInMenu}
          sx={{
            color: 'common.black',
            backgroundColor: 'common.white',
            '&:hover': { backgroundColor: 'grey.200' },
          }}
          endIcon={<Iconify icon="material-symbols:arrow-outward" />}
        >
          Preview Job Page
        </Button>
        <Select
          id="status-select"
          sx={(theme) => {
            const paletteColor = statusOptions.find((option) => option.value === status)?.color;

            if (!paletteColor) {
              return {
                backgroundImage: 'none',
                background: 'none',
                backgroundColor: theme.vars.palette.common.white,
                minWidth: renderInMenu ? '100%' : { xs: '100%', sm: 150 },
                color: theme.vars.palette.common.black,
                ...statusSelectNotchedOutline(
                  varAlpha(theme.vars.palette.grey['500Channel'], 0.2),
                  varAlpha(theme.vars.palette.grey['500Channel'], 0.32)
                ),
              };
            }

            if (paletteColor === 'grey') {
              return {
                backgroundImage: 'none',
                background: 'none',
                backgroundColor: varAlpha(theme.vars.palette.grey['500Channel'], 0.18),
                minWidth: renderInMenu ? '100%' : { xs: '100%', sm: 150 },
                color: theme.vars.palette.grey[800],
                fontWeight: 'bold',
                ...statusSelectNotchedOutline(
                  varAlpha(theme.vars.palette.grey['500Channel'], 0.2),
                  varAlpha(theme.vars.palette.grey['500Channel'], 0.32)
                ),
                ...theme.applyStyles('dark', {
                  color: theme.vars.palette.grey[100],
                }),
              };
            }

            return {
              backgroundImage: 'none',
              background: 'none',
              backgroundColor: varAlpha(theme.vars.palette[paletteColor].mainChannel, 0.18),
              minWidth: renderInMenu ? '100%' : { xs: '100%', sm: 150 },
              color: theme.vars.palette[paletteColor].darker,
              fontWeight: 'bold',
              ...statusSelectNotchedOutline(
                varAlpha(theme.vars.palette[paletteColor].mainChannel, 0.32),
                varAlpha(theme.vars.palette[paletteColor].mainChannel, 0.48)
              ),
              ...theme.applyStyles('dark', {
                color: theme.vars.palette[paletteColor].light,
              }),
            };
          }}
          value={status}
          onChange={(e) => onStatusChange(e.target.value)}
        >
          {statusOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </>
    );

    return (
      <Box sx={{ display: renderInMenu ? 'block' : { xs: 'none', lg: 'block' } }}>
        <Stack direction={renderInMenu ? 'column' : 'row'} spacing={1}>
          {rightAreaContent}
        </Stack>
      </Box>
    );
  }
);

MainLayoutRightArea.displayName = 'MainLayoutRightArea';
